import { api } from '../../lib/api';
import type { PlayableTrack } from '../../stores/player';

/**
 * Jams: a few people, one queue.
 *
 * Every call answers with the whole document, because every change to a jam
 * is a change to something the page is already showing - the members, the
 * queue - and one shape for all of them is one shape to keep right.
 */
export interface JamMember {
  id: number;
  username: string;
  display_name: string;
  verified: boolean;
  avatar: string | null;
  is_host: boolean;
}

/**
 * What a jam is seeded with: the host's queue as they had it, and where
 * they are in it. `kind` and `label` name the thing for the queue's
 * "Next from Late Nights" line.
 */
export interface JamSeed {
  kind: 'collection' | 'tracks';
  id?: number | null;
  label?: string | null;
  track_ids: number[];
  index: number;
  position_ms: number;
  playing: boolean;
}

export interface JamItem {
  item_id: number;
  position: number;
  /** `context`: what the jam plays through. `added`: put there by somebody. */
  kind: 'context' | 'added';
  added_by: { id: number; username: string } | null;
  track: PlayableTrack;
}

/** Where the jam is, as absolute server time. See `JamWatcher` for how it is followed. */
export interface JamPlayback {
  item_id: number | null;
  playing: boolean;
  position_ms: number;
  at: number | null;
  server_time: number;
}

export interface Jam {
  id: number;
  code: string;
  status: 'open' | 'ended';
  host_id: number;
  created_at: string;
  ended_at: string | null;
  playback: JamPlayback;
  is_host: boolean;
  is_member: boolean;
  /** The host's switch: may guests pause, skip and seek for everybody. */
  guests_control: boolean;
  /** The host's other switch: the queue starts again rather than stopping. */
  repeats: boolean;
  members: JamMember[];
  queue: JamItem[];
  /** What the jam is playing through, or null for a jam that is only what people added. */
  context: { kind: 'collection' | 'tracks'; id: number | null; label: string | null } | null;
}

/** The jam the signed-in person is in. One key everywhere, so one refetch reaches every surface. */
export const MY_JAM_KEY = ['jam', 'mine'];

export const jams = {
  mine: () => api.get<{ jam: Jam | null }>('/me/jam'),
  /** A jam, empty or seeded from what is playing (see `startJam`). */
  create: (seed?: JamSeed) => api.post<Jam>('/jams', seed ?? {}),
  /** The host played something else: the jam plays through that now. */
  setContext: (code: string, seed: JamSeed) => api.put<Jam>(`/jams/${code}/context`, seed),
  get: (code: string) => api.get<Jam>(`/jams/${code}`),
  join: (code: string) => api.post<Jam>(`/jams/${code}/join`, {}),
  leave: (code: string) => api.post<{ left: boolean }>(`/jams/${code}/leave`, {}),
  /**
   * Drag one waiting track in front of another; `before: null` is the
   * back of the queue. Refused for anything at or behind the clock - see
   * the backend's `MoveJamItemAction`.
   */
  move: (code: string, itemId: number, before: number | null) =>
    api.put<Jam>(`/jams/${code}/queue/${itemId}/move`, { before }),
  /** Host only. */
  settings: (code: string, patch: { guests_control?: boolean; repeats?: boolean }) =>
    api.patch<Jam>(`/jams/${code}`, patch),
  /** Into the up-next layer: at the back of it, or at the front with `next`. */
  add: (code: string, trackId: number, next = false) =>
    api.post<Jam & { item_id: number }>(`/jams/${code}/queue`, { track_id: trackId, next }),
  remove: (code: string, itemId: number) => api.delete<Jam>(`/jams/${code}/queue/${itemId}`),
  /** Set the clock: what is playing, whether it is, and where it is right now. */
  playback: (code: string, state: { item_id: number | null; playing: boolean; position_ms: number }) =>
    api.post<{ playback: JamPlayback }>(`/jams/${code}/playback`, state),
};

/** The share link, as it goes in a message or on a clipboard. */
export function jamLink(code: string): string {
  return `${window.location.origin}/jam/${code}`;
}
